import moment from "moment";
import { useState, useCallback, useMemo } from "react";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import LTP from "./Ltp";
import { Dialog } from "primereact/dialog";
import BuySell from "../WatchlistUser/BuySell/BuySell";
import useSize from "../../../Utils/useSize/useSize";

const fmt = require("indian-number-format");

const backend = await new Backend().init();

export default function PendingCard({
  item,
  pending_delete,
  reload,
  userType,
}) {
  const windowSize = useSize();

  const [loading, setLoading] = useState(false);

  const [tpassword, setTpassword] = useState("");

  const [buySellModalOpen, setBuySellModalOpen] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);

  // ✅ Helper for backend requests with toast + loader
  const handleRequestCancel = useCallback(
    async (data, successMsg) => {
      try {
        setLoading(true);
        const res = await backend.delete_pending(data);
        if (res.error === "False") {
          toast.success(successMsg || res.message);
          reload?.();
        } else {
          toast.error(res.message || "Request failed");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [reload]
  );

  const handleRequestDelete = useCallback(
    async (data, successMsg) => {
      try {
        setLoading(true);
        const res = await backend.delete(data);
        if (res.error === "False") {
          toast.success(successMsg || res.message);
          reload?.();
        } else {
          toast.error(res.message || "Request failed");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [reload]
  );

  // ✅ Common base payload
  const baseData = useMemo(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    }),
    []
  );

  // ✅ Cancel pending order
  const handleCancel = useCallback(() => {
    const roleMap = {
      1: "admin",
      2: "super",
      3: "master",
      4: "user",
      5: "broker",
      6: "subbroker",
    };
    const data = {
      ...baseData,
      pending_id: item.id,
      [roleMap[userType]]: "1",
    };
    handleRequestCancel(data, "Pending order cancelled");
  }, [item.id, userType, baseData, handleRequestCancel]);

  // ✅ Permanently delete (admin only)
  const handleDelete = useCallback(() => {
    const data = {
      ...baseData,
      delete_select: [item.id],
      type: "PENDING",
      admin: "1",
    };
    handleRequestDelete(data, "Pending order deleted");
  }, [item.id, baseData, handleRequestDelete]);

  // ✅ User edit modal (global function)
  const openUserEdit = (type) => {
    window.user_id = item[`${type}_id`];
    const modalMap = {
      super: window.openeditSuper,
      master: window.masterModalOpen,
      user: window.userModalOpen,
    };
    modalMap[type]?.();
  };

  // ✅ Exchange name
  const exchangeName = useMemo(() => {
    const map = {
      nse: "NSE",
      mcx: "MCX",
      mcx_options: "MCX-OPTIONS",
      fo: "NSE-OPTIONS",
      nse_cash: "NSE-EQUITY",
      comex_future: "COMEX",
      crypto_future: "CRYPTO",
      forex_future: "FOREX",
      us_stock: "US-STOCK",
      sgx: "SGX",
      index: "OTHERS",
      dgcx: "DGCX",
      ncdex: "NCDEX",
    };
    return map[item.type] || "--";
  }, [item.type]);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const handleCancelClick = () => setShowConfirmCancel(true);

  const confirmCancel = () => {
    setShowConfirmCancel(false);
    handleCancel();
  };
  const cancelCancel = () => setShowConfirmCancel(false);

  // ✅ Conditional button disabled logic
  const canCancel = item.status === 0 && !loading;
  const canEdit = item.status === 0 && !loading;
  const isAdmin = userType === 1;

  return (
    <div className="menuContainer addHight text">
      {/* Exchange */}
      <div className="menuExchange small">{exchangeName}</div>

      {/* User hierarchy */}
      {isAdmin && (
        <div onClick={() => openUserEdit("super")} className="menuExchange small">
          <div className="user_id">{item.super_username}</div>
        </div>
      )}
      {userType <= 2 && (
        <div onClick={() => openUserEdit("master")} className="menuExchange small">
          <div className="user_id">{item.master_username}</div>
        </div>
      )}
      {(userType === 1 || userType === 2 || userType === 3 ||  userType === 5) && (
        <div onClick={() => openUserEdit("user")} className="menuExchange small">
          <div className="user_id">{item.username}</div>
        </div>
      )}

      {/* Symbol */}
      <div className={`menuSymbol small`}>
        {item.symbol}
      </div>

      {/* Expiry */}
      <div className="menuExpiry small">
        {item.type === "nse_cash"
          ? "EQUITY"
          : moment(item.expiry_date).format("DD-MMM")}
      </div>

      {/* BUY / SELL */}
      <div
        className={`menuSellQty ${
          item.buy_sell === 0 ? "buytext" : "selltext"
        }`}
      >
        {item.buy_sell === 0 ? "BUY" : "SELL"}
      </div>

      {/* Qty & Price */}
      <div className="menuBuyPrice small">{parseFloat(item.qty)}</div>
      <div className="menuBuyQty small">{fmt.format(item.price)}</div>

      {/* Order Type */}
      <div className="menuSellQty small">
        {item.high_mid === 0 ? "HIGH-LOW" : "MID"}
      </div>
      <div
        className={`menuSellQty small ${
          item.limit_sl === 0 ? "buytext" : "selltext"
        }`}
      >
        {item.limit_sl === 0 ? "LIMIT" : "STOP-LOSS"}
      </div>

      {/* Status */}
      <div
        style={{ textTransform: "uppercase", textAlign: "center", fontSize: 8 }}
        className={`menuSymbol extraSmall ${
          item.status === 0
            ? "background"
            : item.status === 1
            ? "buytext"
            : "selltext"
        }`}
      >
        {item.status === 0 ? <LTP symbol_id={item.symbol_id} /> : item.msg}
      </div>

      {/* Cancel & Created Date */}
      <div className="menuLtt flex flex-col items-center justify-center">
        <div className="extraSmall">
          {item.date_cancel
            ? moment(item.date_cancel).format("DD-MM-YYYY")
            : "--"}
        </div>
        <div className="small">
          {item.date_cancel
            ? moment(item.date_cancel).format("HH:mm:ss")
            : "--"}
        </div>
      </div>

      <div className="menuLtt flex flex-col items-center justify-center">
        <div className="extraSmall">
          {moment(item.date_created).format("DD-MM-YYYY")}
        </div>
        <div className="small">
          {moment(item.date_created).format("HH:mm:ss")}
        </div>
      </div>

      {/* Edit Button */}
      {userType === 4 ? (
        <div className="menuSellQty">
          <button
            onClick={() => setBuySellModalOpen(true)}
            className={`${"bg-[var(--primary)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
            disabled={!canEdit}
            style={{ opacity: canEdit ? 1 : 0.3 }}
          >
            EDIT
          </button>
        </div>
      ) : null}

      <div className="menuSellQty">
        <button
          onClick={loading ? null : handleCancelClick}
          className={`${"bg-[var(--sell)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
          disabled={!canCancel}
          style={{ opacity: canCancel ? 1 : 0.3 }}
        >
          CANCEL
        </button>
      </div>

      {/* DELETE Button (Admin only) */}
      {pending_delete === 1 && isAdmin && (
        <div className="menuSellQty">
          <button
            onClick={() => setDeleteVisible(true)}
            className={`${"bg-[var(--sell)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
            disabled={loading}
          >
            DELETE
          </button>
        </div>
      )}

      {showConfirmCancel && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <h3 className="logoutTitle">Confirm Cancel Pending</h3>
            <p className="logoutText">
              Are you sure you want to cancel {item.symbol} pending?
            </p>

            <div className="logoutModalButtons">
              <button className="confirmBtn" onClick={confirmCancel}>
                Yes, Confirm
              </button>
              <button className="cancelBtn" onClick={cancelCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        visible={buySellModalOpen}
        closeOnEscape
        closeIcon
        onHide={() => setBuySellModalOpen(false)}
        className="modal"
        id="modal"
        position="center"
        transitionOptions={false}
        focusOnShow={false}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: windowSize[0],
          height: windowSize[1] - 100,
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
          }}
        >
          <BuySell
            buysell={buySellModalOpen}
            symbolData={item}
            close={() => {
              reload();
              setBuySellModalOpen(false);
            }}
            buyselltype={item.buy_sell}
            pending={1}
          />
        </div>
      </Dialog>

      {/* 🔹 Delete Modal */}
      {deleteVisible && (
        <div
          className="customModalOverlay"
          onClick={() => setDeleteVisible(false)}
        >
          <div className="customModal" onClick={(e) => e.stopPropagation()}>
            <h3 className="customModalTitle">⚠️ Clear Data</h3>
            <p className="customModalWarning">
              This action will delete user <b>{item.username}</b> pending{" "}
              <b>{item.symbol}</b> after backup.
            </p>

            <input
              type="password"
              placeholder="Enter Transaction password"
              value={tpassword}
              onChange={(e) => setTpassword(e.target.value)}
              disabled={loading}
              className="customInput"
            />

            <div className="customModalButtons">
              <button
                className="dropbtnNavSub white sell"
                onClick={() => setDeleteVisible(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="dropbtnNavSub white primary"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
